import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ReferralCodeModal } from '@/components/user/ReferralCodeModal';
import { Loader2 } from 'lucide-react';

interface ReferralValidationData {
  valid: boolean;
  message: string;
  practitioner_name?: string;
  practitioner_id?: string;
  code_id?: string;
  discount_percentage?: number;
}

export const ApplyReferralPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [validationData, setValidationData] = useState<ReferralValidationData | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [hasExistingCode, setHasExistingCode] = useState(false);

  const referralCode = searchParams.get('ref');

  useEffect(() => {
    const handleReferralFlow = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If not logged in, redirect to signup with referral code
      if (!user) {
        navigate(`/auth?ref=${referralCode}`, { replace: true });
        return;
      }

      // If no referral code in URL, redirect to app
      if (!referralCode) {
        toast({
          title: 'Invalid Link',
          description: 'No referral code found in the URL.',
          variant: 'destructive',
        });
        navigate('/app', { replace: true });
        return;
      }

      try {
        // Fetch user details to check role and existing referral code
        const userResponse = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/me`, {
          credentials: 'include',
        });

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await userResponse.json();
        setUserRole(userData.role);

        // Check if user already has a referral code applied
        if (userData.referral_code_id) {
          setHasExistingCode(true);
          toast({
            title: 'Referral Code Already Applied',
            description: "You've already applied a referral code and cannot apply another one.",
            variant: 'default',
          });
          navigate('/app', { replace: true });
          return;
        }

        // Check if user is a practitioner
        if (userData.role === 'practitioner') {
          toast({
            title: 'Not Available for Practitioners',
            description: 'Practitioners cannot apply referral codes.',
            variant: 'default',
          });
          navigate('/app', { replace: true });
          return;
        }

        // Validate the referral code
        const validationResponse = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/referrals/validate-code/${referralCode}`,
          {
            credentials: 'include',
          }
        );

        const validationResult = await validationResponse.json();
        setValidationData(validationResult);

        if (!validationResult.valid) {
          toast({
            title: 'Invalid Referral Code',
            description: validationResult.message || 'This referral code is not valid.',
            variant: 'destructive',
          });
          navigate('/app', { replace: true });
          return;
        }

        // Show the modal to prompt user to apply the code
        setShowModal(true);
      } catch (error) {
        console.error('Error handling referral flow:', error);
        toast({
          title: 'Error',
          description: 'Failed to process referral code. Please try again.',
          variant: 'destructive',
        });
        navigate('/app', { replace: true });
      } finally {
        setIsValidating(false);
      }
    };

    handleReferralFlow();
  }, [authLoading, user, referralCode, navigate, toast]);

  const handleSuccess = () => {
    navigate('/app', { replace: true });
  };

  const handleModalClose = (open: boolean) => {
    setShowModal(open);
    if (!open) {
      // If user dismisses modal, redirect to app
      navigate('/app', { replace: true });
    }
  };

  // Show loading state
  if (authLoading || isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Processing referral code...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {validationData && validationData.valid && (
        <ReferralCodeModal
          open={showModal}
          onOpenChange={handleModalClose}
          referralCode={referralCode || ''}
          practitionerName={validationData.practitioner_name || 'Unknown Practitioner'}
          discountPercentage={validationData.discount_percentage}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default ApplyReferralPage;
